import { useState, useEffect } from 'react'
import api from '../../utils/axiosConfig'

const MemberEditRequests = () => {
  const [requests, setRequests] = useState([])
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 })
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState('pending')
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [adminNotes, setAdminNotes] = useState('')
  const [actionType, setActionType] = useState('') // 'approve' or 'reject'
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    fetchStats()
    fetchRequests()
  }, [filterStatus])

  const fetchStats = async () => {
    try {
      const response = await api.get('/client-admin/member-edit-requests/stats')
      if (response.data.success) {
        setStats(response.data.stats)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const fetchRequests = async () => {
    try {
      setLoading(true)
      const response = await api.get('/client-admin/member-edit-requests', {
        params: { status: filterStatus }
      })
      if (response.data.success) {
        setRequests(response.data.requests)
      }
    } catch (error) {
      console.error('Error fetching requests:', error)
      alert('Failed to load edit requests')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (request, action) => {
    setSelectedRequest(request)
    setActionType(action)
    setAdminNotes('')
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedRequest(null)
    setAdminNotes('')
    setActionType('')
  }

  const handleSubmitAction = async () => {
    if (actionType === 'reject' && (!adminNotes.trim() || adminNotes.trim().length < 10)) {
      alert('Please provide a reason for rejection (minimum 10 characters)')
      return
    }

    setProcessing(true)

    try {
      const endpoint = `/client-admin/member-edit-requests/${selectedRequest.id}/${actionType}`
      const response = await api.post(endpoint, {
        admin_notes: adminNotes.trim()
      })

      if (response.data.success) {
        alert(response.data.message)
        handleCloseModal()
        fetchStats()
        fetchRequests()
        window.dispatchEvent(new Event('notifications-updated'))
      }
    } catch (error) {
      console.error(`Error ${actionType}ing request:`, error)
      alert(error.response?.data?.message || `Failed to ${actionType} request`)
    } finally {
      setProcessing(false)
    }
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'badge-warning'
      case 'approved': return 'badge-success'
      case 'rejected': return 'badge-danger'
      default: return 'badge-secondary'
    }
  }

  const getMemberTypeIcon = (type) => {
    switch (type) {
      case 'player': return 'fa-user'
      case 'coach': return 'fa-chalkboard-teacher'
      case 'manager': return 'fa-clipboard-user'
      case 'official': return 'fa-id-badge'
      default: return 'fa-user'
    }
  }

  return (
    <div className="edit-requests-page">
      <div className="page-header">
        <div className="header-content">
          <h1><i className="fas fa-edit"></i> Member Edit Requests</h1>
          <p>Review and manage member edit requests</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card pending">
          <div className="stat-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.pending}</h3>
            <p>Pending</p>
          </div>
        </div>
        <div className="stat-card approved">
          <div className="stat-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.approved}</h3>
            <p>Approved</p>
          </div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-icon">
            <i className="fas fa-times-circle"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.rejected}</h3>
            <p>Rejected</p>
          </div>
        </div>
        <div className="stat-card total">
          <div className="stat-icon">
            <i className="fas fa-list"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.total}</h3>
            <p>Total</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`tab ${filterStatus === 'pending' ? 'active' : ''}`}
          onClick={() => setFilterStatus('pending')}
        >
          <i className="fas fa-clock"></i> Pending ({stats.pending})
        </button>
        <button
          className={`tab ${filterStatus === 'approved' ? 'active' : ''}`}
          onClick={() => setFilterStatus('approved')}
        >
          <i className="fas fa-check-circle"></i> Approved ({stats.approved})
        </button>
        <button
          className={`tab ${filterStatus === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilterStatus('rejected')}
        >
          <i className="fas fa-times-circle"></i> Rejected ({stats.rejected})
        </button>
      </div>

      {/* Requests List */}
      <div className="requests-container">
        {loading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <p>No {filterStatus} requests found</p>
          </div>
        ) : (
          <div className="requests-list">
            {requests.map(request => (
              <div key={request.id} className="request-card">
                <div className="card-header">
                  <div className="member-info">
                    <i className={`fas ${getMemberTypeIcon(request.member_type)} member-icon`}></i>
                    <div>
                      <h3>{request.member_details?.name || 'Unknown Member'}</h3>
                      <p className="member-meta">
                        <span className="member-type">{request.member_details?.type}</span>
                        <span className="separator">•</span>
                        <span className="member-id">{request.member_details?.player_id || request.member_details?.official_id}</span>
                      </p>
                    </div>
                  </div>
                  <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </span>
                </div>

                <div className="card-body">
                  <div className="requester-info">
                    <i className="fas fa-user"></i>
                    <span>Requested by: <strong>{request.requester.name}</strong></span>
                    {request.requester.association_name && (
                      <span className="association">({request.requester.association_name})</span>
                    )}
                  </div>

                  <div className="request-reason">
                    <strong>Reason:</strong>
                    <p>{request.reason}</p>
                  </div>

                  <div className="requested-changes">
                    <strong>Requested Changes:</strong>
                    <div className="changes-grid">
                      {Object.entries(request.requested_changes).map(([field, value]) => (
                        <div key={field} className="change-item">
                          <span className="field-name">{field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                          <span className="field-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {request.admin_notes && (
                    <div className="admin-notes">
                      <strong>Admin Notes:</strong>
                      <p>{request.admin_notes}</p>
                    </div>
                  )}

                  <div className="request-meta">
                    <span className="date">
                      <i className="fas fa-calendar"></i>
                      Submitted: {new Date(request.created_at).toLocaleString()}
                    </span>
                    {request.reviewed_at && (
                      <>
                        <span className="separator">•</span>
                        <span className="date">
                          <i className="fas fa-check"></i>
                          Reviewed: {new Date(request.reviewed_at).toLocaleString()}
                        </span>
                      </>
                    )}
                    {request.reviewer && (
                      <>
                        <span className="separator">•</span>
                        <span>by {request.reviewer.name}</span>
                      </>
                    )}
                  </div>
                </div>

                {request.status === 'pending' && (
                  <div className="card-actions">
                    <button
                      className="btn-reject"
                      onClick={() => handleOpenModal(request, 'reject')}
                    >
                      <i className="fas fa-times"></i> Reject
                    </button>
                    <button
                      className="btn-approve"
                      onClick={() => handleOpenModal(request, 'approve')}
                    >
                      <i className="fas fa-check"></i> Approve
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showModal && selectedRequest && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ color: '#ffffff' }}>
                {actionType === 'approve' ? (
                  <>
                    <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                    Approve Edit Request
                  </>
                ) : (
                  <>
                    <i className="fas fa-times-circle" style={{ color: '#ef4444' }}></i>
                    Reject Edit Request
                  </>
                )}
              </h2>
            </div>

            <div className="modal-body">
              <div className="confirmation-message">
                <p>
                  You are about to <strong>{actionType}</strong> the edit request for:
                </p>
                <div className="member-highlight">
                  <strong>{selectedRequest.member_details?.name}</strong>
                  <span>({selectedRequest.member_details?.type})</span>
                </div>
              </div>

              <div className="changes-summary">
                <h4>Changes to be {actionType === 'approve' ? 'applied' : 'rejected'}:</h4>
                {Object.entries(selectedRequest.requested_changes).map(([field, value]) => (
                  <div key={field} className="change-row">
                    <span className="field">{field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                    <span className="value">{value}</span>
                  </div>
                ))}
              </div>

              <div className="notes-section">
                <label htmlFor="admin-notes">
                  {actionType === 'reject' ? 'Reason for Rejection' : 'Notes (Optional)'}
                  {actionType === 'reject' && <span className="required">*</span>}
                </label>
                <textarea
                  id="admin-notes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={
                    actionType === 'reject'
                      ? 'Please provide a reason for rejection (minimum 10 characters)'
                      : 'Add any notes about this approval (optional)'
                  }
                  rows={4}
                  required={actionType === 'reject'}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={handleCloseModal}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                className={`btn-confirm ${actionType === 'approve' ? 'approve' : 'reject'}`}
                onClick={handleSubmitAction}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Processing...
                  </>
                ) : (
                  <>
                    <i className={`fas fa-${actionType === 'approve' ? 'check' : 'times'}`}></i>
                    {actionType === 'approve' ? 'Approve' : 'Reject'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .edit-requests-page {
          padding: 2rem;
          background: #f9fafb;
          min-height: 100vh;
        }

        .page-header {
          margin-bottom: 2rem;
        }

        .page-header h1 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #111827;
          margin: 0 0 0.5rem 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .page-header h1 i {
          color: #3b82f6;
        }

        .page-header p {
          color: #6b7280;
          margin: 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border-left: 4px solid;
        }

        .stat-card.pending { border-left-color: #f59e0b; }
        .stat-card.approved { border-left-color: #10b981; }
        .stat-card.rejected { border-left-color: #ef4444; }
        .stat-card.total { border-left-color: #3b82f6; }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        .stat-card.pending .stat-icon { background: #fef3c7; color: #f59e0b; }
        .stat-card.approved .stat-icon { background: #d1fae5; color: #10b981; }
        .stat-card.rejected .stat-icon { background: #fee2e2; color: #ef4444; }
        .stat-card.total .stat-icon { background: #dbeafe; color: #3b82f6; }

        .stat-info h3 {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }

        .stat-info p {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0;
          font-weight: 500;
        }

        .filter-tabs {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .filter-tabs .tab {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 600;
          color: #6b7280;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-tabs .tab:hover {
          border-color: #c00f15;
          color: #c00f15;
        }

        .filter-tabs .tab.active {
          background: linear-gradient(135deg, #c00f15 0%, #a00d12 100%);
          border-color: #c00f15;
          color: white;
        }

        .requests-container {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .loading-state,
        .empty-state {
          padding: 4rem 2rem;
          text-align: center;
          color: #6b7280;
        }

        .loading-state i,
        .empty-state i {
          font-size: 3rem;
          margin-bottom: 1rem;
          color: #3b82f6;
        }

        .requests-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .request-card {
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s;
        }

        .request-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .card-header {
          padding: 1.25rem 1.5rem;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .member-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .member-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #dbeafe;
          color: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
        }

        .member-info h3 {
          margin: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
        }

        .member-meta {
          margin: 0.25rem 0 0;
          font-size: 0.875rem;
          color: #6b7280;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .member-type {
          font-weight: 500;
          color: #3b82f6;
        }

        .separator {
          color: #d1d5db;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 9999px;
          font-size: 0.8125rem;
          font-weight: 600;
        }

        .badge-warning {
          background: #fef3c7;
          color: #92400e;
        }

        .badge-success {
          background: #d1fae5;
          color: #065f46;
        }

        .badge-danger {
          background: #fee2e2;
          color: #991b1b;
        }

        .card-body {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .requester-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .requester-info i {
          color: #3b82f6;
        }

        .association {
          color: #9ca3af;
          font-style: italic;
        }

        .request-reason strong,
        .requested-changes strong,
        .admin-notes strong {
          display: block;
          margin-bottom: 0.5rem;
          color: #374151;
          font-size: 0.9375rem;
        }

        .request-reason p,
        .admin-notes p {
          margin: 0;
          color: #6b7280;
          line-height: 1.6;
          background: #f9fafb;
          padding: 0.75rem 1rem;
          border-radius: 6px;
        }

        .changes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 0.75rem;
        }

        .change-item {
          background: #f9fafb;
          padding: 0.75rem;
          border-radius: 6px;
          border-left: 3px solid #3b82f6;
        }

        .field-name {
          display: block;
          font-size: 0.75rem;
          font-weight: 500;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.25rem;
        }

        .field-value {
          display: block;
          font-weight: 600;
          color: #111827;
        }

        .request-meta {
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          font-size: 0.8125rem;
          color: #6b7280;
        }

        .request-meta i {
          color: #3b82f6;
          margin-right: 0.25rem;
        }

        .card-actions {
          padding: 1rem 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .btn-reject,
        .btn-approve {
          padding: 0.625rem 1.25rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          border: none;
        }

        .btn-reject {
          background: #fee2e2;
          color: #991b1b;
        }

        .btn-reject:hover {
          background: #fecaca;
        }

        .btn-approve {
          background: #d1fae5;
          color: #065f46;
        }

        .btn-approve:hover {
          background: #a7f3d0;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          padding: 2rem 2rem 1.5rem;
          border-bottom: 2px solid #f3f4f6;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #111827;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .modal-body {
          padding: 2rem;
        }

        .confirmation-message {
          margin-bottom: 1.5rem;
        }

        .confirmation-message p {
          margin: 0 0 1rem 0;
          color: #6b7280;
        }

        .member-highlight {
          background: #f3f4f6;
          padding: 1rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.125rem;
        }

        .changes-summary {
          background: #f9fafb;
          padding: 1.25rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .changes-summary h4 {
          margin: 0 0 1rem 0;
          color: #374151;
          font-size: 0.9375rem;
          font-weight: 600;
        }

        .change-row {
          padding: 0.5rem 0;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
        }

        .change-row:last-child {
          border-bottom: none;
        }

        .change-row .field {
          font-weight: 500;
          color: #6b7280;
        }

        .change-row .value {
          font-weight: 600;
          color: #111827;
        }

        .notes-section label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #374151;
        }

        .required {
          color: #ef4444;
          margin-left: 0.25rem;
        }

        .notes-section textarea {
          width: 100%;
          padding: 0.875rem;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-family: inherit;
          font-size: 0.9375rem;
          resize: vertical;
          transition: all 0.2s;
        }

        .notes-section textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .modal-actions {
          padding: 1.5rem 2rem 2rem;
          display: flex;
          gap: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel,
        .btn-confirm {
          flex: 1;
          padding: 0.875rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          border: none;
        }

        .btn-cancel {
          background: #f3f4f6;
          color: #6b7280;
        }

        .btn-cancel:hover:not(:disabled) {
          background: #e5e7eb;
          color: #374151;
        }

        .btn-confirm.approve {
          background: #10b981;
          color: white;
        }

        .btn-confirm.approve:hover:not(:disabled) {
          background: #059669;
        }

        .btn-confirm.reject {
          background: #ef4444;
          color: white;
        }

        .btn-confirm.reject:hover:not(:disabled) {
          background: #dc2626;
        }

        .btn-confirm:disabled,
        .btn-cancel:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .edit-requests-page {
            padding: 1rem;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }

          .filter-tabs {
            flex-direction: column;
          }

          .changes-grid {
            grid-template-columns: 1fr;
          }

          .card-actions {
            flex-direction: column-reverse;
          }

          .btn-reject,
          .btn-approve {
            width: 100%;
            justify-content: center;
          }

          .modal-actions {
            flex-direction: column-reverse;
          }
        }
      `}</style>
    </div>
  )
}

export default MemberEditRequests
